import { useMemo } from 'react';
import { useProjectTasks } from './useProjectTasks';

export const useOverviewTables = (project) => {
  const { tasks, loading: tasksLoading, refreshTasks } = useProjectTasks(project?._id);

  const formatRowDate = (d) =>
    d instanceof Date && !Number.isNaN(d.getTime())
      ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      : '—';

  const formatRowDateTime = (d) =>
    d instanceof Date && !Number.isNaN(d.getTime())
      ? d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : '—';

  const transactionRows = useMemo(() => {
    const out = [];
    // 1. Advance ledger entry removed to only show original database transactions
    // 2. Transactions from backend summary
    const txs = project?.paymentSummary?.transactions || [];
    for (const t of txs) {
      let d = null;
      if (t.transactionDate && t.createdAt) {
        const txDate = new Date(t.transactionDate);
        const created = new Date(t.createdAt);
        // transactionDate is stored as UTC midnight. Extract the UTC date, 
        // and combine it with the actual local time it was recorded.
        d = new Date(
          txDate.getUTCFullYear(),
          txDate.getUTCMonth(),
          txDate.getUTCDate(),
          created.getHours(),
          created.getMinutes()
        );
      } else {
        d = t.transactionDate ? new Date(t.transactionDate) : t.createdAt ? new Date(t.createdAt) : null;
      }

      if (t.kind === 'credit') {
        if (t.openingAdvance) continue;
        const details = [
          t.paymentType,
          t.bankName ? `Bank: ${t.bankName}` : null,
          t.upiPhone ? `UPI: ${t.upiPhone}` : null,
          t.chequeNumber ? `Cheque: ${t.chequeNumber}` : null,
          t.receiverName ? `Receiver: ${t.receiverName}` : null,
          t.recipient
        ].filter(Boolean).join(' · ') || 'Payment received';

        out.push({
          key: t._id,
          typeLabel: 'Credit',
          typeClass: 'bg-emerald-50 text-emerald-700',
          description: details,
          refId: t.refId?.trim() ? t.refId : '—',
          method: '—',
          amount: Number(t.amount) || 0,
          amountClass: 'text-emerald-600',
          date: d,
          status: 'Approved',
          roleType: '—',
          siteName: project?.siteName || '—',
          expenseType: t.paymentType || 'Credit'
        });
      } else if (t.kind === 'expense') {
        const st = t.requestStatus || 'pending';
        // Only show paid expenses in this transactions list
        if (st !== 'paid') continue;

        const isPending = st === 'pending';
        const bookAmt = isPending ? Number(t.amount) || 0 : (t.approvedAmount ?? t.amount);
        
        out.push({
          key: t._id,
          typeLabel: isPending ? 'Expense (pending)' : 'Expense',
          typeClass: isPending ? 'bg-amber-50 text-amber-800' : 'bg-rose-50 text-rose-700',
          description: t.expenseReason || t.categoryLabel || 'Expense request',
          refId: '—',
          method: 'Project expense',
          amount: Number(bookAmt) || 0,
          amountClass: isPending ? 'text-amber-700' : 'text-rose-600',
          date: d,
          status: st.charAt(0).toUpperCase() + st.slice(1),
          // New fields
          roleType: t.roleType?.replace(/_/g, ' ') || '—',
          siteName: project?.siteName || '—',
          expenseType: (t.epType && t.epType !== 'not_applicable') ? t.epType.replace(/_/g, ' ') : (t.expenseCategory || '—')
        });
      } else if (t.kind === 'follow-up') {
        const st = t.requestStatus || 'pending';
        out.push({
          key: t._id,
          typeLabel: 'Follow-up',
          typeClass: 'bg-indigo-50 text-indigo-700',
          description: t.expenseReason || 'Payment follow-up',
          refId: '—',
          method: 'Follow-up request',
          amount: Number(t.amount) || 0,
          amountClass: 'text-indigo-600',
          date: d,
          status: st === 'pending' ? 'Awaiting' : st.charAt(0).toUpperCase() + st.slice(1),
          roleType: t.roleType?.replace(/_/g, ' ') || '—',
          siteName: project?.siteName || '—',
          expenseType: (t.epType && t.epType !== 'not_applicable') ? t.epType.replace(/_/g, ' ') : (t.expenseCategory || '—')
        });
      }
    }
    out.sort((a, b) => (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0));
    return out;
  }, [project]);

  return {
    tasks,
    tasksLoading,
    refreshTasks,
    transactionRows,
    formatRowDate,
    formatRowDateTime
  };
};

